import { Inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { KieneUtils } from '../../../utils/KieneUtils';
import { GruppeTier } from '../../gruppe-tier/classes/gruppe-tier';
import { EinstallenMelden, EinstallenMeldenDialogInput } from '../classes/einstallen-melden';
import { Gruppe } from '../classes/gruppe';
import { ScanErgebnis } from '../classes/scan-ergebnis';
import { EinstallenService } from './einstallen.service';
import { GruppenService } from '../gruppen.service';
import { ZugangGewichteDialogComponent } from './zugang-gewichte-dialog/zugang-gewichte-dialog.component';
import { AppService } from '../../../services/app/app.service';

export interface EinstallenHitOptionen {
    passUndOhrmarke: boolean;
    nurPassGescannt: boolean;
    nurOhrmarkeGescannt: boolean;
    wederPassNochOhrmarke: boolean;
}

export interface EinstallenMeldenOverview {
    anzahlPassUndOhrmarke: number;
    anzahlPass: number;
    anzahlOhrmarke: number;
    anzahlCsv: number;
    nichtMeldbar: number;
}

export interface EinstallenMeldenOverviewOptions {
    einstallTierlisteId?: number | null;
    /** When true, only animals from the current Einstall scan session (no tierliste). */
    scopeToSessionScans?: boolean;
    wholeGroup?: boolean;
}

export interface EinstallenMeldenSubmitInput {
    gruppeId: number;
    gsId?: number;
    optionen: EinstallenHitOptionen;
    einstallTierlisteId?: number | null;
    wholeGroup?: boolean;
    scopeToSessionScans?: boolean;
}

const HIT_MELDEN_PASS_UND_OHRMARKE = 'hit_melden_pass_und_ohrmarke';
const HIT_MELDEN_NUR_PASS_GESCANNT = 'hit_melden_nur_pass_gescannt';
const HIT_MELDEN_NUR_OHRMARKE_GESCANNT = 'hit_melden_nur_ohrmarke_gescannt';
const HIT_MELDEN_WEDER_PASS_NOCH_OHRMARKE = 'hit_melden_weder_pass_noch_ohrmarke';

@Injectable({ providedIn: 'root' })
export class EinstallenSubmitFlowService {
    private readonly gewichtPermissionId = 9161;

    constructor(
        private readonly dialog: MatDialog,
        private readonly gruppenService: GruppenService,
        private readonly messageService: MessageService,
        private readonly api: KieneBackendApiService,
        private readonly einstallenService: EinstallenService,
        private readonly localStorageService: LocalStorageService,
        private readonly appService: AppService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    currentUserCanEditGewicht(): boolean {
        return this.localStorageService.hasPermission(this.gewichtPermissionId);
    }

    getLastSuccessfulScans(): ScanErgebnis[] {
        return this.deduplicateScansByGtId(this.filterGewichtRelevantScans(this.einstallenService.getLastScanErgebnisse()));
    }

    getMissingGewichtScans(scans?: ScanErgebnis[]): ScanErgebnis[] {
        const relevantScans = scans?.length ? scans : this.getLastSuccessfulScans();
        return relevantScans.filter(scan => !scan?.einstallgewicht || Number(scan.einstallgewicht) <= 0);
    }

    getLastSuccessfulScansCount(scans?: ScanErgebnis[]): number {
        return (scans?.length ? scans : this.getLastSuccessfulScans()).length;
    }

    getLastSuccessfulScansGesamtGewicht(scans?: ScanErgebnis[]): number {
        const relevantScans = scans?.length ? scans : this.getLastSuccessfulScans();
        return relevantScans.reduce((sum, scan) => sum + (Number(scan?.einstallgewicht) || 0), 0);
    }

    loadGewichtRelevantScans(gruppeId: number, einstallTierlisteId?: number | null): Observable<ScanErgebnis[]> {
        const tierlisteId = Number(einstallTierlisteId || this.einstallenService.getCurrentEinstallTierlisteId() || 0);
        if (tierlisteId > 0 && gruppeId > 0) {
            return this.einstallenService.loadEinstallTierlisteTiere(gruppeId, tierlisteId, null).pipe(
                map(scans =>
                    this.applyPendingGewichteToScans(this.filterGewichtRelevantScans(scans || [])),
                ),
            );
        }

        return of(this.applyPendingGewichteToScans(this.getLastSuccessfulScans()));
    }

    getCurrentEinstallTierlisteId(): number | null {
        return this.einstallenService.getCurrentEinstallTierlisteId();
    }

    getEinstallMeldenOverviewOptions(einstallTierlisteId?: number | null): EinstallenMeldenOverviewOptions {
        const tierlisteId = Number(einstallTierlisteId ?? 0);
        if (tierlisteId > 0) {
            return { einstallTierlisteId: tierlisteId };
        }
        return { scopeToSessionScans: true };
    }

    /** Shared submit input for desktop stepper, mobile review and melden dialog. */
    buildMeldenSubmitInput(
        optionen: EinstallenHitOptionen,
        gruppe?: Pick<Gruppe, 'gruppe_id' | 'gs_id'> | null,
        options?: { wholeGroup?: boolean },
    ): EinstallenMeldenSubmitInput {
        const currentGruppe = this.einstallenService.getCurrentGruppe();
        const wholeGroup = !!options?.wholeGroup;
        const einstallTierlisteId = wholeGroup ? null : this.einstallenService.getCurrentEinstallTierlisteId();

        return {
            gruppeId: Number(gruppe?.gruppe_id || currentGruppe?.gruppe_id || 0),
            gsId: this.resolveGsId(gruppe?.gs_id ?? currentGruppe?.gs_id),
            optionen,
            einstallTierlisteId,
            wholeGroup,
            scopeToSessionScans: wholeGroup ? false : !einstallTierlisteId,
        };
    }

    canSubmitMelden(optionen: EinstallenHitOptionen, overview: EinstallenMeldenOverview): boolean {
        return this.hasMeldbareTiereForOptionen(overview, optionen);
    }

    loadHitOptionen(): Observable<EinstallenHitOptionen> {
        return forkJoin({
            passUndOhrmarke: this.api.getSpeicher(HIT_MELDEN_PASS_UND_OHRMARKE).pipe(map((value: any) => !!value)),
            nurPassGescannt: this.api.getSpeicher(HIT_MELDEN_NUR_PASS_GESCANNT).pipe(map((value: any) => !!value)),
            nurOhrmarkeGescannt: this.api.getSpeicher(HIT_MELDEN_NUR_OHRMARKE_GESCANNT).pipe(map((value: any) => !!value)),
            wederPassNochOhrmarke: this.api.getSpeicher(HIT_MELDEN_WEDER_PASS_NOCH_OHRMARKE).pipe(map((value: any) => !!value)),
        }) as Observable<EinstallenHitOptionen>;
    }

    saveHitOptionen(optionen: EinstallenHitOptionen): void {
        this.api.setSpeicher(HIT_MELDEN_PASS_UND_OHRMARKE, optionen.passUndOhrmarke);
        this.api.setSpeicher(HIT_MELDEN_NUR_PASS_GESCANNT, optionen.nurPassGescannt);
        this.api.setSpeicher(HIT_MELDEN_NUR_OHRMARKE_GESCANNT, optionen.nurOhrmarkeGescannt);
        this.api.setSpeicher(HIT_MELDEN_WEDER_PASS_NOCH_OHRMARKE, optionen.wederPassNochOhrmarke);
    }

    ensureFullTierlisteForReview(gruppeId: number, einstallTierlisteId?: number | null): Observable<void> {
        const tierlisteId = Number(
            einstallTierlisteId ?? this.einstallenService.getCurrentEinstallTierlisteId() ?? 0,
        );
        if (tierlisteId <= 0 || gruppeId <= 0) {
            return of(undefined);
        }

        return this.einstallenService.loadEinstallTierlisteTiere(gruppeId, tierlisteId, null).pipe(
            tap(scans => this.einstallenService.setTierlisteScanErgebnisse(scans || [])),
            map(() => undefined),
        );
    }

    loadMeldenOverview(gruppeId: number, options?: EinstallenMeldenOverviewOptions): Observable<EinstallenMeldenOverview> {
        const einstallTierlisteId = Number(options?.einstallTierlisteId ?? 0);
        const useTierlisteScope = einstallTierlisteId > 0;
        const sessionGtIds =
            !options?.wholeGroup && !useTierlisteScope && options?.scopeToSessionScans
                ? this.getSessionMeldenGtIds()
                : [];

        let params = new HttpParams();
        if (useTierlisteScope) {
            params = params.set('einstall_tierliste_id', String(einstallTierlisteId));
        }

        return this.gruppenService.ladeGruppenTiere(gruppeId, params).pipe(
            switchMap(response => {
                let records = this.filterMeldenOverviewRecords(response?.records || [], sessionGtIds);
                if (records.length > 0 || sessionGtIds.length === 0) {
                    return of(this.buildMeldenOverview(this.filterUnreportedRecords(records)));
                }

                return this.gruppenService.ladeGruppenTiere(gruppeId).pipe(
                    map(fallback =>
                        this.buildMeldenOverview(
                            this.filterUnreportedRecords(
                                this.filterMeldenOverviewRecords(fallback?.records || [], sessionGtIds),
                            ),
                        ),
                    ),
                );
            }),
        );
    }

    deriveHitOptionenFromOverview(overview: EinstallenMeldenOverview): EinstallenHitOptionen {
        return {
            passUndOhrmarke: overview.anzahlPassUndOhrmarke > 0,
            nurPassGescannt: overview.anzahlPass > 0,
            nurOhrmarkeGescannt: overview.anzahlOhrmarke > 0,
            wederPassNochOhrmarke: overview.anzahlCsv > 0,
        };
    }

    openGewichteDialog(gruppeId: number, scanErgebnisse?: ScanErgebnis[], onlyMissing = false): Observable<boolean> {
        const scans = scanErgebnisse?.length
            ? this.deduplicateScansByGtId(scanErgebnisse)
            : this.getLastSuccessfulScans();
        const mobile = this.appService.isMobileScreen;
        return this.dialog
            .open(ZugangGewichteDialogComponent, {
                width: mobile ? 'calc(100vw - 24px)' : '760px',
                maxWidth: mobile ? 'calc(100vw - 24px)' : '95vw',
                maxHeight: '85vh',
                panelClass: mobile ? 'gewicht-dialog-panel--mobile' : undefined,
                data: {
                    gruppeId: gruppeId,
                    gsId: this.resolveGsId(this.einstallenService.getCurrentGruppe()?.gs_id),
                    onlyMissing: onlyMissing,
                    scanErgebnisse: scans,
                },
            })
            .afterClosed();
    }

    async submitHitMelden(input: EinstallenMeldenSubmitInput): Promise<boolean> {
        const overview = await firstValueFrom(
            this.loadMeldenOverview(input.gruppeId, this.buildMeldenOverviewOptions(input)),
        );
        if (!this.hasMeldbareTiereForOptionen(overview, input.optionen)) {
            this.messageService.alertMessage('Keine ungemeldeten Tiere für die ausgewählten Kategorien vorhanden.');
            return false;
        }

        this.saveHitOptionen(input.optionen);

        const payload = new EinstallenMelden();
        payload.gruppe_id = input.gruppeId;
        payload.pass_und_ohrmarke = input.optionen.passUndOhrmarke ? 1 : 0;
        payload.nur_pass_gescannt = input.optionen.nurPassGescannt ? 1 : 0;
        payload.nur_ohrmarke_gescannt = input.optionen.nurOhrmarkeGescannt ? 1 : 0;
        payload.weder_pass_noch_ohrmarke = input.optionen.wederPassNochOhrmarke ? 1 : 0;

        const einstallTierlisteId = input.wholeGroup ? 0 : Number(input.einstallTierlisteId ?? 0);
        const pendingGewichte = this.getPendingGewichteForMelden(einstallTierlisteId);
        const gsId = this.resolveGsId(input.gsId ?? this.einstallenService.getCurrentGruppe()?.gs_id);

        if (pendingGewichte.length > 0 && !gsId) {
            this.messageService.alertMessage(
                'Einstallgewichte konnten nicht gespeichert werden: Gruppenstandort (gs_id) fehlt.',
            );
            return false;
        }

        if (pendingGewichte.length > 0 && gsId) {
            await this.savePendingGewichteSequential(pendingGewichte, gsId);
        }
        await firstValueFrom(this.gruppenService.einstallenMelden(payload));

        let tierlisteAbgeschlossen = true;
        if (einstallTierlisteId > 0) {
            try {
                await firstValueFrom(
                    this.api.post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', {
                        tierliste_id: einstallTierlisteId,
                        status_id: 163,
                    }, undefined),
                );
            } catch {
                tierlisteAbgeschlossen = false;
                this.messageService.hintMessage(
                    'Die Tiere werden an HI-Tier gemeldet. Die Einstall-Liste konnte nicht abgeschlossen werden — bitte Seite aktualisieren.',
                );
            }
            this.einstallenService.clearCurrentEinstallTierlisteId();
        }
        try {
            await firstValueFrom(this.gruppenService.closeReportedEinstallTierlisten(input.gruppeId));
        } catch {
            if (einstallTierlisteId <= 0) {
                tierlisteAbgeschlossen = false;
                this.messageService.hintMessage(
                    'Die Tiere werden an HI-Tier gemeldet. Offene Einstall-Listen konnten nicht automatisch abgeschlossen werden — bitte Seite aktualisieren.',
                );
            }
        }

        this.einstallenService.clearPendingGewichte();
        this.einstallenService.clearCurrentEinstalldatum();
        if (tierlisteAbgeschlossen) {
            this.messageService.infoMessage('Die Tiere werden im Hintergrund an HI-Tier gemeldet.');
        }
        return true;
    }

    async closeEinstallTierlisteWithoutMelden(input: {
        gruppeId: number;
        gsId?: number;
        einstallTierlisteId?: number | null;
    }): Promise<boolean> {
        const einstallTierlisteId = Number(input.einstallTierlisteId || this.einstallenService.getCurrentEinstallTierlisteId() || 0);
        const pendingGewichte = this.getPendingGewichteForMelden(einstallTierlisteId);
        const gsId = this.resolveGsId(input.gsId ?? this.einstallenService.getCurrentGruppe()?.gs_id);

        if (pendingGewichte.length > 0 && !gsId) {
            this.messageService.alertMessage(
                'Einstallgewichte konnten nicht gespeichert werden: Gruppenstandort (gs_id) fehlt.',
            );
            return false;
        }

        if (pendingGewichte.length > 0 && gsId) {
            await this.savePendingGewichteSequential(pendingGewichte, gsId);
        }

        if (einstallTierlisteId > 0) {
            await firstValueFrom(
                this.api.post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', {
                    tierliste_id: einstallTierlisteId,
                    status_id: 163,
                }, undefined),
            );
            this.einstallenService.clearCurrentEinstallTierlisteId();
        }

        this.einstallenService.clearPendingGewichte();
        this.einstallenService.clearCurrentEinstalldatum();
        this.messageService.infoMessage('Die Einstall-Liste wurde abgeschlossen.');
        return true;
    }

    createMeldenDialogInput(gruppeId: number, gsId?: number, submitLockMs?: number): EinstallenMeldenDialogInput {
        const input = new EinstallenMeldenDialogInput();
        input.gruppe_id = gruppeId;
        input.gs_id = this.resolveGsId(gsId);
        input.submitLockMs = submitLockMs;
        return input;
    }

    private filterGewichtRelevantScans(scans: ScanErgebnis[] | null | undefined): ScanErgebnis[] {
        return (scans || []).filter(
            scan => !!scan?.success && !!scan?.gt_id && !scan?.bereitsInGruppe && !scan?.importFailed,
        );
    }

    private buildMeldenOverview(records: GruppeTier[]): EinstallenMeldenOverview {
        const overview: EinstallenMeldenOverview = {
            anzahlPassUndOhrmarke: 0,
            anzahlPass: 0,
            anzahlOhrmarke: 0,
            anzahlCsv: 0,
            nichtMeldbar: 0,
        };

        records.forEach((tier: GruppeTier) => {
            const pendingGewicht = this.einstallenService.getPendingGewichtForTier(tier?.gt_id);
            if (pendingGewicht && pendingGewicht > 0) {
                tier.einstallgewicht = pendingGewicht;
            }

            const ohrmarkeGescannt = Number(tier.ohrmarke_gescannt) || 0;
            const passGescannt = Number(tier.pass_gescannt) || 0;

            if (KieneUtils.isNoU(tier.kp_einstalldatum) && KieneUtils.isNoU(tier.hit_einstalldatum)) {
                overview.nichtMeldbar++;
            } else if (ohrmarkeGescannt >= 1 && passGescannt >= 1) {
                overview.anzahlPassUndOhrmarke++;
            } else if (ohrmarkeGescannt >= 1 && passGescannt === 0) {
                overview.anzahlOhrmarke++;
            } else if (ohrmarkeGescannt === 0 && passGescannt >= 1) {
                overview.anzahlPass++;
            } else {
                overview.anzahlCsv++;
            }
        });

        return overview;
    }

    private resolveGsId(gsId?: number | null): number | undefined {
        const resolved = Number(gsId || 0);
        return resolved > 0 ? resolved : undefined;
    }

    private getPendingGewichteForMelden(einstallTierlisteId: number): Array<{ gt_id: number; gewicht: number }> {
        if (einstallTierlisteId > 0) {
            const tierlisteGtIds = new Set(
                this.einstallenService
                    .getLastScanErgebnisse()
                    .map(scan => Number(scan?.gt_id))
                    .filter(id => id > 0),
            );
            const pending = this.einstallenService.getPendingGewichte();
            if (tierlisteGtIds.size > 0) {
                return pending.filter(entry => tierlisteGtIds.has(entry.gt_id));
            }
            return pending;
        }
        return this.einstallenService.getPendingGewichteForActiveScans();
    }

    private async savePendingGewichteSequential(
        pendingGewichte: Array<{ gt_id: number; gewicht: number }>,
        gsId: number,
    ): Promise<void> {
        await firstValueFrom(this.einstallenService.persistEinstallgewichte(pendingGewichte, gsId));
    }

    private getSessionMeldenGtIds(): number[] {
        return [
            ...new Set(
                this.getLastSuccessfulScans()
                    .map(scan => Number(scan?.gt_id))
                    .filter(id => id > 0),
            ),
        ];
    }

    private filterMeldenOverviewRecords(records: GruppeTier[], sessionGtIds: number[]): GruppeTier[] {
        if (!sessionGtIds.length) {
            return records;
        }
        const idSet = new Set(sessionGtIds);
        return records.filter(tier => idSet.has(Number(tier?.gt_id)));
    }

    private deduplicateScansByGtId(scans: ScanErgebnis[]): ScanErgebnis[] {
        const byGtId = new Map<number, ScanErgebnis>();
        for (const scan of scans || []) {
            const gtId = Number(scan?.gt_id);
            if (!gtId || !scan?.success) {
                continue;
            }
            const existing = byGtId.get(gtId);
            byGtId.set(gtId, existing ? this.mergeScanErgebnisseForGewicht(existing, scan) : scan);
        }
        return Array.from(byGtId.values());
    }

    private mergeScanErgebnisseForGewicht(existing: ScanErgebnis, next: ScanErgebnis): ScanErgebnis {
        const merged = Object.assign(new ScanErgebnis(), existing, next);
        merged.pass_gescannt = Math.max(Number(existing.pass_gescannt) || 0, Number(next.pass_gescannt) || 0);
        merged.ohrmarke_gescannt = Math.max(
            Number(existing.ohrmarke_gescannt) || 0,
            Number(next.ohrmarke_gescannt) || 0,
        );

        const existingWeight = Number(existing.einstallgewicht) || 0;
        const nextWeight = Number(next.einstallgewicht) || 0;
        merged.einstallgewicht = existingWeight > 0 ? existingWeight : nextWeight > 0 ? nextWeight : undefined;

        const pendingGewicht = this.einstallenService.getPendingGewichtForTier(Number(merged.gt_id));
        if (pendingGewicht && pendingGewicht > 0) {
            merged.einstallgewicht = pendingGewicht;
        }

        return merged;
    }

    private applyPendingGewichteToScans(scans: ScanErgebnis[]): ScanErgebnis[] {
        return (scans || []).map(scan => {
            const pendingGewicht = this.einstallenService.getPendingGewichtForTier(Number(scan?.gt_id));
            if (!pendingGewicht || pendingGewicht <= 0) {
                return scan;
            }
            const withPending = Object.assign(new ScanErgebnis(), scan);
            withPending.einstallgewicht = pendingGewicht;
            return withPending;
        });
    }

    private filterUnreportedRecords(records: GruppeTier[]): GruppeTier[] {
        return records.filter(tier => this.isTierNochZuMelden(tier));
    }

    private isTierNochZuMelden(tier: GruppeTier): boolean {
        const status = Number(tier?.status_id_einstallen);
        if (!status) {
            return true;
        }
        if (status === 140 || status === 143) {
            return true;
        }
        return false;
    }

    private buildMeldenOverviewOptions(input: {
        einstallTierlisteId?: number | null;
        wholeGroup?: boolean;
        scopeToSessionScans?: boolean;
    }): EinstallenMeldenOverviewOptions {
        if (input.wholeGroup) {
            return { wholeGroup: true };
        }
        const einstallTierlisteId = Number(input.einstallTierlisteId ?? 0);
        if (einstallTierlisteId > 0) {
            return { einstallTierlisteId };
        }
        if (input.scopeToSessionScans) {
            return { scopeToSessionScans: true };
        }
        return {};
    }

    private hasMeldbareTiereForOptionen(
        overview: EinstallenMeldenOverview,
        optionen: EinstallenHitOptionen,
    ): boolean {
        return (
            (optionen.passUndOhrmarke && overview.anzahlPassUndOhrmarke > 0) ||
            (optionen.nurPassGescannt && overview.anzahlPass > 0) ||
            (optionen.nurOhrmarkeGescannt && overview.anzahlOhrmarke > 0) ||
            (optionen.wederPassNochOhrmarke && overview.anzahlCsv > 0)
        );
    }
}
